import { Directive, HostBinding, HostListener, Input } from '@angular/core';

type ImageLoading = 'lazy' | 'eager';
type ImageDecoding = 'sync' | 'async' | 'auto';
type FetchPriority = 'high' | 'low' | 'auto';

@Directive({
  selector: 'img[appOptimizedImage]'
})
export class OptimizedImageDirective {
  @Input() appOptimizedImageFallback: string | null = null;
  @Input() appOptimizedImageLoading: ImageLoading = 'lazy';
  @Input() appOptimizedImageDecoding: ImageDecoding = 'async';
  @Input() appOptimizedImageFetchPriority: FetchPriority = 'auto';
  @Input() appOptimizedImageSrcset: string | null = null;
  @Input() appOptimizedImageSizes: string | null = null;
  @Input() appOptimizedImageWidth: number | string | null = null;
  @Input() appOptimizedImageHeight: number | string | null = null;

  @HostBinding('attr.loading')
  get loading(): ImageLoading {
    return this.appOptimizedImageLoading;
  }

  @HostBinding('attr.decoding')
  get decoding(): ImageDecoding {
    return this.appOptimizedImageDecoding;
  }

  @HostBinding('attr.fetchpriority')
  get fetchPriority(): FetchPriority {
    return this.appOptimizedImageFetchPriority;
  }

  @HostBinding('attr.srcset')
  get srcset(): string | null {
    return this.appOptimizedImageSrcset || null;
  }

  @HostBinding('attr.sizes')
  get sizes(): string | null {
    return this.appOptimizedImageSizes || null;
  }

  @HostBinding('attr.width')
  get width(): number | string | null {
    return this.appOptimizedImageWidth ?? null;
  }

  @HostBinding('attr.height')
  get height(): number | string | null {
    return this.appOptimizedImageHeight ?? null;
  }

  @HostListener('error', ['$event'])
  onImageError(event: Event): void {
    const fallback = (this.appOptimizedImageFallback || '').trim();
    if (!fallback) {
      return;
    }

    const image = event?.target as HTMLImageElement | null;
    if (!image) {
      return;
    }

    const currentSrc = image.getAttribute('src') || '';
    if (currentSrc === fallback) {
      return;
    }

    image.setAttribute('src', fallback);
    image.src = fallback;
    image.removeAttribute('srcset');
  }
}
